<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'

import clsx from 'clsx'
import { type UseDraggableReturn, VueDraggable } from 'vue-draggable-plus'

import BetaBaseImage from '@/components/beta-editor/BetaBaseImage.vue'
import BetaEditorElement from '@/components/beta-editor/BetaEditorElement.vue'
import BetaImportedSectionPreview from '@/components/beta-editor/BetaImportedSectionPreview.vue'
import BetaEditorSectionRail from '@/components/beta-editor/BetaEditorSectionRail.vue'
import BetaStoreSectionPreview from '@/components/beta-editor/BetaStoreSectionPreview.vue'
import { isDark } from '@/utils/color'
import { useEditorBetaStore } from '@/stores/editor/beta'
import { useEditorPageStore } from '@/stores/editor/page'
import { useLayoutStore } from '@/stores/layout'
import { isImportedHomePreviewCandidate } from '@/utils/editor-home-preview'

const props = defineProps<{
    section: any
    sectionIndex: number
    hasDelete: boolean
    isEditorDisabled: boolean
    class?: string
}>()

const betaStore = useEditorBetaStore()
const editorPageStore = useEditorPageStore()
const layoutStore = useLayoutStore()

const section = ref<any>(reactive({}))
const extra = ref<any>(reactive({}))
const content = ref<any[]>([])
const draggable = ref<UseDraggableReturn>()

watch(
    () => props.section,
    value => {
        section.value = reactive(value ?? {})
        content.value = section.value.frameContent ?? []
        extra.value = reactive(value?.extra ?? {})
    },
    { deep: true, immediate: true },
)

watch(content, value => {
    section.value.frameContent = value
}, { deep: true })

const getTypeSection = (component: string) => {
    if (component.includes('home')) return 'home'
    if (component.includes('blog')) return 'listblog'
    if (component.includes('contact')) return 'contact'
    if (component.includes('store')) return 'storelist'
    if (component.includes('about')) return 'about'
    if (component.includes('comments')) return 'comments'
    if (component.includes('maincontent')) return 'maincontent'
    if (component.includes('post')) return 'post'
    if (component.includes('newsletter')) return 'newsletter'

    return
}

const isHome = computed(() => props.section.component?.includes('home'))
const isBlog = computed(() => props.section.component?.includes('blog'))
const isContact = computed(() => props.section.component?.includes('contact'))
const isStore = computed(() => props.section.component?.includes('store'))
const isMaincontent = computed(() => props.section.component?.includes('maincontent'))
const isPost = computed(() => props.section.component?.includes('post'))
const isAbout = computed(() => props.section.component?.includes('about'))
const isComments = computed(() => props.section.component?.includes('comments'))
const isNewsletter = computed(() => props.section.component?.includes('newsletter'))
const isEditable = computed(() => isMaincontent.value || isPost.value)
const notEditable = computed(() => isHome.value || isBlog.value || isContact.value || isStore.value || isAbout.value || isComments.value || isNewsletter.value)
const validateSection = computed(() => {
    if (isHome.value && props.sectionIndex === 0) return false
    if (editorPageStore.activePage?.isHome && props.sectionIndex !== 0) return true

    return true
})

const container = computed(() => {
    if (isMaincontent.value && section.value.extra?.maincontent_container) {
        return section.value.extra?.maincontent_container
    }

    const type = getTypeSection(section.value.component ?? '')
    const id = section.value.frameId ?? 0

    if (type) {
        const resolvedContainer = layoutStore.getContainer(id, type, type + '_container')

        if (resolvedContainer) return resolvedContainer
    }

    return 'container'
})

const fontWeight = computed(() => layoutStore.getSectionFontWeightDefault(props.sectionIndex))
const isCustomBackgroundColor = computed(() => extra.value.background_color && extra.value.background_color !== '')
const backgroundColor = computed(() => (
    isCustomBackgroundColor.value
        ? extra.value.background_color
        : layoutStore.getSectionBackgroundColorDefault(props.sectionIndex)
))
const isDarkMode = computed(() => isDark(backgroundColor.value))
const backgroundImage = computed(() => section.value.backgroundImage?.uri ?? null)
const backgroundImageOpacity = computed(() => extra.value.background_image_opacity ?? '80')
const backgroundFocalPoint = computed(() => extra.value.background_image_focal_point ?? { x: 50, y: 50 })
const dynamicStyleSection = computed(() => ({
    backgroundColor: backgroundColor.value,
    fontWeight: fontWeight.value,
}))
const isSectionSelected = computed(() => betaStore.isSelectedSection(section.value, props.sectionIndex))

const getSchema = (frameType: string, index: number) => [
    { level: 0, element: 'section', index: props.sectionIndex },
    { level: 1, element: frameType, index },
]

function getTextCssFromJson(currentSection: any, component: string): Record<string, string> {
    const styleKeys = [
        'font_size',
        'font_weight',
        'line_height',
        'letter_spacing',
        'text_align',
        'text_transform',
        'vertical_align',
        'margin',
        'margin_bottom',
        'margin_top',
        'padding',
        'padding_bottom',
        'padding_top',
    ]
    const result: Record<string, string> = {}

    styleKeys.forEach((key) => {
        const composedKey = `${component}[${key}]`
        const value = currentSection.extra?.[composedKey]

        if (value !== undefined && value !== null && value !== '') {
            result[key.replace('_', '-')] = value
        }
    })

    return result
}

const titleStyles = computed(() => getTextCssFromJson(props.section, 'title'))
const importedPage = computed(() => {
    if (!editorPageStore.activePage?.isHome) return null

    return betaStore.getImportedSectionPreviewMeta(section.value.frameId ?? null)
})
const isImportedToHome = computed(() => (
    Boolean(editorPageStore.activePage?.isHome && isImportedHomePreviewCandidate(section.value))
))

const stripHtml = (value?: string) => (typeof value === 'string' ? value.replace(/<[^>]*>/g, '').trim() : '')

const findFirstFrameContentByType = (node: any, frameType: string): any => {
    if (!node) return null

    if (node.frameType === frameType) return node.frameContent

    const children = Array.isArray(node.frameContent)
        ? node.frameContent
        : Array.isArray(node.frameContent?.contents)
            ? node.frameContent.contents
            : []

    for (const child of children) {
        const found = findFirstFrameContentByType(child, frameType)

        if (found !== null && found !== undefined && found !== '') return found
    }

    return null
}

const importedPreviewPosts = computed(() => {
    const hydratedPosts = betaStore.getImportedSubpagesPreviewContent(section.value.frameId ?? null, null)

    if (Array.isArray(hydratedPosts)) return hydratedPosts

    const savedPosts = findFirstFrameContentByType(section.value, 'subpages')

    return Array.isArray(savedPosts) ? savedPosts : []
})
const importedPreviewTitle = computed(() => (
    stripHtml(findFirstFrameContentByType(section.value, 'title'))
    || importedPage.value?.name
    || 'Seção importada'
))

const onSelectSection = () => {
    if (betaStore.shouldKeepActiveTextSelection()) {
        return
    }

    if (!validateSection.value) {
        betaStore.clearSelectedSection()
        betaStore.clearSidebarTarget()
        return
    }

    betaStore.setSelectedSection(section.value, props.sectionIndex)
    betaStore.clearSelectedElement()
    betaStore.clearEditingElement()
    betaStore.setElementForEdit(null)
    betaStore.setSidebarReference(isEditable.value ? 'section' : null)
}

const onAppendTextElement = () => {
    betaStore.appendTextElementToSection(props.sectionIndex)
}
</script>

<template>
    <section
        :id="`${section.frameId || section.temporaryId}`"
        class="editor-beta-section"
        :data-section-active="isSectionSelected"
        :data-preview-device="betaStore.previewDevice"
        :class="clsx(props.class)"
        :style="{ ...dynamicStyleSection }"
        @pointerdown.self="onSelectSection"
    >
        <beta-base-image
            v-if="backgroundImage"
            :src="backgroundImage"
            :alt="`Background Image ${props.sectionIndex}`"
            class-name="absolute inset-0 h-full w-full object-cover object-center"
            :style="{
                opacity: `${backgroundImageOpacity / 100}`,
                objectPosition: `${backgroundFocalPoint.x}% ${backgroundFocalPoint.y}%`,
            }"
        />

        <beta-editor-section-rail
            v-if="validateSection"
            :section="section"
            :section-index="props.sectionIndex"
            :has-config="isEditable"
            :has-delete="props.hasDelete"
            :is-active="isSectionSelected"
        />

        <div :class="clsx(container && container)" @pointerdown.self="onSelectSection">
            <beta-store-section-preview
                v-if="isStore"
            />

            <beta-imported-section-preview
                v-else-if="isImportedToHome"
                :page-name="importedPage?.name ?? importedPreviewTitle"
                :title="importedPreviewTitle"
                :posts="importedPreviewPosts"
            />

            <VueDraggable
                v-else-if="isEditable"
                ref="draggable"
                v-model="content"
                class="editor-elements"
                :animation="150"
                handle=".beta-action-move-element"
                group="elements"
                ghostClass="ghost"
            >
                <template v-for="(element, index) in content" :key="element?.frameId || element?.temporaryId">
                    <beta-editor-element
                        v-if="element"
                        :element="element"
                        :element-index="index"
                        :section-index="props.sectionIndex"
                        :section-background-color="backgroundColor"
                        :section-has-background-image="!!backgroundImage"
                        :is-editable="true"
                        :schema="getSchema(element.frameType, index)"
                        :heading-style="titleStyles"
                    />
                </template>
            </VueDraggable>

            <div v-else-if="notEditable" class="editor-elements">
                <template v-for="(element, index) in section.frameContent" :key="element.frameId || element.temporaryId || index">
                    <beta-editor-element
                        :element="element"
                        :element-index="index"
                        :section-index="props.sectionIndex"
                        :section-background-color="backgroundColor"
                        :section-has-background-image="!!backgroundImage"
                        :is-home="isHome"
                        :is-about="isAbout"
                        :is-editable="false"
                        :schema="getSchema(element.frameType, index)"
                        :heading-style="titleStyles"
                    />
                </template>
            </div>

            <div v-if="isEditable" class="editor-beta-section-footer">
                <button class="editor-beta-section-footer__button" @click.stop="onAppendTextElement">
                    Adicionar texto
                </button>
            </div>
        </div>
    </section>
</template>

<style scoped>
.editor-beta-section {
    @apply relative overflow-visible border border-transparent py-16 transition-all duration-1000 ease-in-out;
    max-width: 100%;
}

.editor-beta-section[data-section-active='true'] {
    border-color: rgba(109, 161, 255, 0.72);
    box-shadow: 0 0 0 4px rgba(186, 216, 255, 0.85), 0 28px 60px rgba(15, 23, 42, 0.12);
}

.editor-beta-section[data-preview-device='mobile'] {
    @apply rounded-[24px] py-10;
}

.editor-beta-section .container,
.editor-beta-section .container-medium,
.editor-beta-section .container-fluid {
    @apply relative mx-auto min-w-0 max-w-full px-6;
    box-sizing: border-box;
}

.editor-beta-section[data-preview-device='mobile'] .container,
.editor-beta-section[data-preview-device='mobile'] .container-medium,
.editor-beta-section[data-preview-device='mobile'] .container-fluid {
    @apply px-5;
}

.container {
    @apply max-w-[540px] md:max-w-[720px] lg:max-w-[960px] xl:max-w-[1140px];
}

.container-medium {
    @apply max-w-[1280px];
}

.editor-elements {
    @apply flex min-w-0 max-w-full flex-wrap justify-center;
    box-sizing: border-box;
    transition:
        min-height 1000ms ease-in-out,
        gap 1000ms ease-in-out;
}

.editor-beta-section-footer {
    @apply mt-8 flex justify-center;
}

.editor-beta-section-footer__button {
    @apply rounded-full border border-[#DCE5F2] bg-white px-4 py-2 text-sm font-semibold text-[#51657F] shadow-sm transition-colors duration-200 hover:bg-[#F4F7FB];
}

.editor-imported-section-badge {
    @apply absolute -top-14 left-6 z-20 inline-flex max-w-[calc(100%-7rem)] items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-medium;
    border-color: rgba(15, 23, 42, 0.03);
    background: rgba(255, 255, 255, 0.38);
    backdrop-filter: blur(8px);
    color: rgba(71, 85, 105, 0.68);
}

.editor-imported-section-badge__link {
    @apply truncate font-semibold;
    max-width: 180px;
    color: rgba(17, 24, 39, 0.64);
}

.ghost {
    opacity: 0.5;
    background: #c8ebfb;
}
</style>
